import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Send, X, ArrowUp } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_COMMANDS = [
  "Create a TikTok campaign for my restaurant",
  "What can you do?",
  "Schedule posts for next week"
];

/**
 * FloatingActionButton Component - WITH WORKING VOICE COMMAND
 */
export default function FloatingActionButton({ selectedLanguage }) {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Text command with streaming
  const sendCommand = async (text = message) => {
    if (!text.trim()) return;
    setLoading(true);
    setResponse("");
    
    try {
      const fetchResponse = await fetch(`${API}/text/command/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text, language: selectedLanguage })
      });
      
      if (!fetchResponse.ok) throw new Error(`HTTP ${fetchResponse.status}`);
      
      const reader = fetchResponse.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResponse = "";
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'chunk') {
                accumulatedResponse += data.content;
                setResponse(accumulatedResponse);
              } else if (data.type === 'done') {
                setResponse(accumulatedResponse);
              }
            } catch (e) { /* Skip malformed JSON */ }
          }
        }
      }
      
      setMessage("");
    } catch (error) {
      console.error("FAB command error:", error);
      toast.error("Failed to process command. Please try again.");
      setResponse("");
    } finally {
      setLoading(false);
    }
  };

  // Voice command processing - RESTORED
  const processVoiceCommand = async (audioBlob) => {
    setLoading(true);
    setResponse("");
    
    try {
      const base64Audio = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      const fetchResponse = await fetch(`${API}/voice/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio_base64: base64Audio,
          language: selectedLanguage
        })
      });
      
      if (!fetchResponse.ok) throw new Error('Voice processing failed');
      
      const data = await fetchResponse.json();
      
      if (data.transcribed_text) {
        setMessage(data.transcribed_text);
        toast.success(`Heard: "${data.transcribed_text}"`);
      }
      
      if (data.response) {
        setResponse(data.response);
      } else if (data.transcribed_text) {
        // Fallback to text command
        await sendCommand(data.transcribed_text);
      } else {
        setResponse("I couldn't understand that. Please try again or type your command.");
      }
    } catch (error) {
      console.error("Voice error:", error);
      toast.error("Voice processing failed. Please try typing.");
      setResponse("Voice processing failed. Please type your command.");
    } finally {
      setLoading(false);
    }
  };

  // Store mimeType for blob creation
  const mimeTypeRef = useRef('audio/webm');

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use webm with opus codec, fallback to default
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mimeTypeRef.current = mimeType;
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
        if (audioBlob.size > 0) {
          toast.info("Processing voice...");
          
          // Use FormData for more reliable file upload
          try {
            setLoading(true);
            const formData = new FormData();
            const fileExtension = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
            formData.append('audio', audioBlob, `recording.${fileExtension}`);
            formData.append('language', 'en');
            
            const response = await fetch(`${API}/voice/command-file`, {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Voice processing failed');
            }
            
            const data = await response.json();
            
            if (data.transcribed_text) {
              setMessage(data.transcribed_text);
              toast.success(`Heard: "${data.transcribed_text}"`);
            }
            
            if (data.response) {
              setResponse(data.response);
            }
          } catch (error) {
            console.error("Voice error:", error);
            toast.error(error.message || "Voice processing failed. Please type your command.");
          } finally {
            setLoading(false);
          }
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success("🎤 Recording... Tap again to stop");
    } catch (error) {
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* FAB */}
      <div className="fixed bottom-6 left-6 z-50 flex flex-col items-start gap-3">
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="glass-card rounded-2xl p-4 w-80 border border-white/10 shadow-2xl mb-2"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Mic className="w-4 h-4 text-primary" />
                  </div>
                  <span className="font-semibold text-sm">YASNO Assistant</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} className="h-6 w-6 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Voice Orb */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={loading}
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center transition-all
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                    ${isRecording 
                      ? 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50' 
                      : 'bg-gradient-to-br from-primary to-blue-600 hover:shadow-lg hover:shadow-primary/50'
                    }
                  `}
                  data-testid="fab-voice-orb"
                >
                  {isRecording ? (
                    <MicOff className="w-6 h-6 text-white" />
                  ) : (
                    <Mic className="w-6 h-6 text-white" />
                  )}
                </button>
              </div>

              {/* Text Input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  placeholder="Type a command..."
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendCommand()}
                  disabled={loading}
                  data-testid="fab-input"
                />
                <Button onClick={() => sendCommand()} disabled={!message.trim() || loading} size="sm" data-testid="fab-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>

              {/* Quick Commands */}
              <div className="space-y-1">
                {QUICK_COMMANDS.map((cmd) => (
                  <button
                    key={cmd}
                    onClick={() => {
                      setMessage(cmd);
                      sendCommand(cmd);
                    }}
                    disabled={loading}
                    className="block w-full text-left text-xs p-2 rounded bg-white/5 hover:bg-white/10 transition-colors text-muted-foreground disabled:opacity-50"
                  >
                    {cmd}
                  </button>
                ))}
              </div>

              {/* Response */}
              {(response || loading) && (
                <div className="mt-3 p-3 rounded-lg bg-primary/10 border border-primary/20 max-h-48 overflow-y-auto">
                  <p className="text-xs font-medium text-primary mb-1">YASNO:</p>
                  {response ? (
                    <p className="text-xs whitespace-pre-line">
                      {response}
                      {loading && <span className="inline-block w-1 h-3 bg-primary ml-0.5 animate-pulse" />}
                    </p>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="flex gap-0.5">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" />
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      Thinking...
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main FAB Button - MIC ICON */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setExpanded(!expanded)}
          className={`
            w-14 h-14 rounded-full shadow-lg flex items-center justify-center relative
            bg-gradient-to-br from-primary to-blue-600 hover:shadow-xl hover:shadow-primary/30 transition-shadow
          `}
          data-testid="fab-button"
        >
          {expanded ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Mic className="w-6 h-6 text-white" />
          )}
          
          {!expanded && (
            <span className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-30" />
          )}
        </motion.button>

        {!expanded && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-muted-foreground whitespace-nowrap"
          >
            Speak to Agent
          </motion.span>
        )}
      </div>

      {/* Scroll to Top Button - Left side, above the FAB */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-36 left-6 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition-colors"
            data-testid="scroll-top-btn"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
